const React = require('react')
const { useState } = require('react')
const { useParams, useHistory } = require('react-router-dom')
const { UncontrolledTooltip, Table } = require('reactstrap')
const { useNetworkConfig } = require('@contexts')
const { useFormatBytes, useFormatTimestampShort, useFormatDateTime, useResponsive, BREAKPOINTS } = require('@hooks')
const { LoadingSpinner, CopyButton } = require('@components')
const { TableHeader } = require('@components/table')

require('@styles/pages/address/transactions.address.css')

const AddressInternalTransactions = props => {
    const { loading, data } = props
    const { address } = useParams()
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestampShort()
    const formatDateTime = useFormatDateTime()
    const [showAge, setShowAge] = useState(true)
    const isMobile = useResponsive(BREAKPOINTS.extraLarge)

    return (
        <div className='box table-section'>
            {loading ? (
                <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
                    <LoadingSpinner />
                </div>
            ) : (
                <>
                    {data?.internalTransactions.totalItems > 0 && (
                        <TableHeader
                            title={
                                <>
                                    Latest&nbsp;
                                    {data?.internalTransactions.totalItems > 25
                                        ? '25'
                                        : data?.internalTransactions.totalItems}{' '}
                                    internal transaction{data?.internalTransactions.totalItems > 1 ? 's' : ''}&nbsp;
                                    {data?.internalTransactions.totalItems > 25 && (
                                        <>
                                            (
                                            <span
                                                className={`${network.name}-text-color`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() =>
                                                    history.push(`/${network.name}/txsInternal?a=${address}`)
                                                }
                                            >
                                                View All
                                            </span>
                                            )
                                        </>
                                    )}
                                </>
                            }
                            usePagination={false}
                        />
                    )}
                    <Table
                        responsive
                        style={isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}}
                    >
                        <thead>
                            <tr>
                                <th>Parent Transaction Hash</th>
                                <th>Block</th>
                                <th>
                                    <span
                                        id='time-format-tooltip'
                                        className={`${network.name}-text-color cursor-pointer`}
                                        onClick={() => setShowAge(!showAge)}
                                    >
                                        {showAge ? 'Age' : 'Date Time (UTC)'}
                                    </span>
                                    <UncontrolledTooltip placement='top' target='time-format-tooltip'>
                                        {showAge ? 'Click to show Datetime Format' : 'Click to show Age Format'}
                                    </UncontrolledTooltip>
                                </th>
                                <th>From</th>
                                <th>To</th>
                                <th>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data?.internalTransactions.items.map(transaction => {
                                return (
                                    <tr key={transaction.hash}>
                                        <td>
                                            {!transaction.status && (
                                                <>
                                                    <div
                                                        className='tx-status-indicator'
                                                        style={{ color: 'var(--fail-color)' }}
                                                        id={`error-${transaction.hash}`}
                                                    >
                                                        <i className='fas fa-times' />
                                                    </div>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`error-${transaction.hash}`}
                                                    >
                                                        Error in Txn: execution reverted
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                            <span
                                                id={`parent-tx-hash-${transaction.hash}`}
                                                className={`${network.name}-text-color cursor-pointer`}
                                                onClick={() => history.push(`/${network.name}/tx/${transaction.hash}`)}
                                            >
                                                {formatBytes(transaction.hash, 20, 15, 0)}
                                            </span>
                                            <UncontrolledTooltip
                                                placement='top'
                                                target={`parent-tx-hash-${transaction.hash}`}
                                            >
                                                {transaction.hash}
                                            </UncontrolledTooltip>
                                            &nbsp;
                                            <CopyButton
                                                id={`copy-parent-tx-hash-${transaction.hash}`}
                                                data={transaction.hash}
                                                text={'Copy Transaction Hash'}
                                            />
                                        </td>
                                        <td>
                                            <span
                                                className={`${network.name}-text-color cursor-pointer`}
                                                onClick={() =>
                                                    history.push(`/${network.name}/block/${transaction.blockNumber}`)
                                                }
                                            >
                                                {transaction.blockNumber}
                                            </span>
                                        </td>
                                        <td>
                                            {showAge ? (
                                                <>
                                                    <span id={'age-' + transaction.hash}>
                                                        {formatTimestamp(transaction.blockTimestamp)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`age-${transaction.hash}`}
                                                    >
                                                        {formatDateTime(transaction.blockTimestamp)}
                                                    </UncontrolledTooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <span id={'datetime-' + transaction.blockNumber}>
                                                        {formatDateTime(transaction.blockTimestamp)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`datetime-${transaction.blockNumber}`}
                                                    >
                                                        {formatTimestamp(transaction.blockTimestamp)}
                                                    </UncontrolledTooltip>
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {transaction.from.type === 'SCA' && (
                                                <>
                                                    <i
                                                        className='fas fa-file-alt'
                                                        style={{
                                                            fontSize: '14px',
                                                            color: 'var(--grey-color)'
                                                        }}
                                                    />
                                                    &nbsp;
                                                </>
                                            )}
                                            <span
                                                id={`from-${transaction.hash}`}
                                                className={
                                                    transaction.from.address !== address &&
                                                    `${network.name}-text-color cursor-pointer`
                                                }
                                                onClick={() =>
                                                    transaction.from.address !== address &&
                                                    history.push(`/${network.name}/address/${transaction.from.address}`)
                                                }
                                            >
                                                {formatBytes(transaction.from.address, 20, 9, 6)}
                                            </span>
                                            <UncontrolledTooltip placement='top' target={`from-${transaction.hash}`}>
                                                {transaction.from.address}
                                            </UncontrolledTooltip>
                                            &nbsp;
                                            <CopyButton
                                                id={`copy-from-${transaction.hash}`}
                                                data={transaction.from.address}
                                                text={'Copy Address'}
                                            />
                                        </td>
                                        <td>
                                            {transaction.to.type === 'SCA' && (
                                                <>
                                                    <i
                                                        className='fas fa-file-alt'
                                                        style={{
                                                            fontSize: '14px',
                                                            color: 'var(--grey-color)'
                                                        }}
                                                    />
                                                    &nbsp;
                                                </>
                                            )}
                                            <span
                                                id={`to-${transaction.hash}`}
                                                className={
                                                    transaction.to.address !== address &&
                                                    `${network.name}-text-color cursor-pointer`
                                                }
                                                onClick={() =>
                                                    transaction.to.address !== address &&
                                                    history.push(`/${network.name}/address/${transaction.to.address}`)
                                                }
                                            >
                                                {formatBytes(transaction.to.address, 20, 9, 6)}
                                            </span>
                                            <UncontrolledTooltip placement='top' target={`to-${transaction.hash}`}>
                                                {transaction.to.address}
                                            </UncontrolledTooltip>
                                            &nbsp;
                                            <CopyButton
                                                id={`copy-to-${transaction.hash}`}
                                                data={transaction.to.address}
                                                text={'Copy Address'}
                                            />
                                        </td>
                                        <td>
                                            {transaction.value / 10 ** 18} {network.data.token}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>

                    {data?.internalTransactions.totalItems > 25 && (
                        <div
                            className={`view-all ${network.name}-text-color-after`}
                            onClick={() => history.push(`/${network.name}/txsInternal?a=${address}`)}
                        >
                            View all internal transactions
                            <i className='fas fa-arrow-right' />
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

module.exports = { AddressInternalTransactions }
